import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowLeft,
  FaBolt,
  FaChartLine,
  FaClipboardCheck,
  FaClock,
  FaFolderOpen,
  FaSignInAlt,
  FaTrophy,
  FaUpload,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { KpiCard } from "./components/KpiCard";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { PartialDataBanner } from "./components/PartialDataBanner";
import { HealthBadge } from "./components/HealthBadge";
import { Avatar } from "./components/Avatar";
import { EnvToggle } from "./components/EnvToggle";
import { useEnv } from "./useEnv";
import { dateRangeParams, fetchUserProfile } from "./api";
import "./OrgUsageAnalytics.css";

const formatDate = (ms) => {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
};

const formatRelative = (ms) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return "—";
  }
};

const fmtN = (n) =>
  typeof n === "number" ? n.toLocaleString() : n ?? "0";

const chartTooltipProps = {
  contentStyle: {
    background: "#fff",
    border: "1px solid #e6e9f1",
    borderRadius: 8,
    fontSize: 12,
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
  },
  labelStyle: { color: "#64748b", fontWeight: 600, fontSize: 11 },
};

function SkeletonKpis() {
  return (
    <div className="ou-kpi-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="ou-kpi">
          <div className="ou-skel ou-skel-line" style={{ width: "55%" }} />
          <div
            className="ou-skel"
            style={{ height: 28, width: "45%", margin: "10px 0 6px" }}
          />
          <div className="ou-skel ou-skel-line" style={{ width: "40%" }} />
        </div>
      ))}
    </div>
  );
}

export default function UserUsageProfilePage() {
  const { organizationId, userId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const orgName = location.state?.organizationName || organizationId;
  const displayName =
    location.state?.userName || location.state?.userEmail || userId;

  const [env, setEnv] = useEnv();
  // Default to 7 days: fewer history rows to read = faster first load. Wider
  // windows are still one click away via DateRangeFilter.
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [partial, setPartial] = useState(false);

  const range = useMemo(() => dateRangeParams(days), [days]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await fetchUserProfile(organizationId, userId, range, env);
      if (!json.success) {
        toast.error(json.reason || "User not found");
        setProfile(null);
        return;
      }
      setProfile(json.data);
      setPartial(Boolean(json.partial));
    } catch (err) {
      toast.error(err.message || "Failed to load user profile");
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId, range, env]);

  useEffect(() => {
    load();
  }, [load]);

  const user = profile?.user;
  const metrics = profile?.metrics;
  const vs = profile?.vsOrgAverage;

  return (
    <div className="ou-page">
      <ToastContainer position="top-right" autoClose={4000} />

      <nav className="ou-breadcrumb">
        <Link to="/org-usage-analytics">User Usage</Link>
        <span className="ou-breadcrumb-sep">/</span>
        <Link
          to={{
            pathname: `/org-usage-analytics/${organizationId}`,
            state: { organizationName: orgName },
          }}
        >
          {orgName}
        </Link>
        <span className="ou-breadcrumb-sep">/</span>
        <span style={{ color: "var(--ou-text)", fontWeight: 500 }}>
          {user?.name || displayName}
        </span>
      </nav>
      <button
        type="button"
        className="ou-back-btn"
        onClick={() =>
          history.push(`/org-usage-analytics/${organizationId}`, {
            organizationName: orgName,
          })
        }
      >
        <FaArrowLeft /> Back to {orgName}
      </button>

      <header className="ou-header">
        <div className="ou-header-left ou-profile-strip">
          <Avatar
            name={user?.name || displayName}
            seed={userId}
            size="lg"
          />
          <div className="ou-profile-meta">
            <h1 className="ou-profile-name">{user?.name || displayName}</h1>
            <div className="ou-profile-sub">
              {user?.email ? <span>{user.email}</span> : null}
              {user?.role ? (
                <>
                  <span className="ou-dot" />
                  <span className="ou-role-pill">{user.role}</span>
                </>
              ) : null}
              {user?.activityStatus ? (
                <>
                  <span className="ou-dot" />
                  <HealthBadge health={user.activityStatus} />
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <EnvToggle env={env} onChange={setEnv} />
          <DateRangeFilter days={days} onChange={setDays} />
        </div>
      </header>

      <PartialDataBanner show={partial} />

      {loading && !profile ? (
        <SkeletonKpis />
      ) : !profile ? (
        <div className="ou-empty">
          <div className="ou-empty-title">No profile data available</div>
          <div className="ou-empty-sub">
            This user may not exist in this organization or has no recorded activity.
          </div>
        </div>
      ) : (
        <>
          <div className="ou-kpi-grid">
            <KpiCard
              label="Total events"
              value={fmtN(metrics?.totalEvents)}
              icon={<FaChartLine />}
              accent="indigo"
              subtext={
                vs?.events != null ? `Org avg: ${fmtN(vs.events)}` : null
              }
            />
            <KpiCard
              label="Loans opened"
              value={fmtN(metrics?.loansAccessedCount)}
              icon={<FaFolderOpen />}
              accent="cyan"
              subtext={
                vs?.loansAccessed != null
                  ? `Org avg: ${fmtN(vs.loansAccessed)}`
                  : null
              }
            />
            <KpiCard
              label="Manual runs"
              value={fmtN(metrics?.checklistRunsCount)}
              icon={<FaClipboardCheck />}
              accent="green"
              subtext="Manual AI runs (excl. autoruns)"
            />
            <KpiCard
              label="Docs uploaded"
              value={fmtN(metrics?.documentsUploadedCount)}
              icon={<FaUpload />}
              accent="pink"
              subtext="Documents added"
            />
            <KpiCard
              label="Rank in org"
              value={metrics?.rankInOrg ? `#${metrics.rankInOrg}` : "—"}
              icon={<FaTrophy />}
              accent="amber"
              subtext={
                metrics?.activeUsersInOrg
                  ? `of ${fmtN(metrics.activeUsersInOrg)} active`
                  : null
              }
            />
            <KpiCard
              label="Last active"
              value={formatRelative(user?.lastActiveAt)}
              icon={<FaClock />}
              accent="rose"
              subtext={user?.lastActiveAt ? formatDate(user.lastActiveAt) : null}
            />
          </div>

          <div className="ou-highlight-grid">
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--indigo">
                <FaBolt />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Events vs org average</div>
                <div className="ou-highlight-value">
                  {fmtN(metrics?.totalEvents)}{" "}
                  <span
                    style={{
                      color: "var(--ou-dim)",
                      fontWeight: 500,
                      fontSize: 13,
                    }}
                  >
                    vs {fmtN(vs?.events ?? 0)}
                  </span>
                </div>
                <div className="ou-highlight-sub">
                  {metrics?.totalEvents > (vs?.events ?? 0)
                    ? "Above average"
                    : metrics?.totalEvents === (vs?.events ?? 0)
                      ? "At average"
                      : "Below average"}
                </div>
              </div>
            </div>
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--green">
                <FaFolderOpen />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Loans vs org average</div>
                <div className="ou-highlight-value">
                  {fmtN(metrics?.loansAccessedCount ?? 0)}{" "}
                  <span
                    style={{
                      color: "var(--ou-dim)",
                      fontWeight: 500,
                      fontSize: 13,
                    }}
                  >
                    vs {fmtN(vs?.loansAccessed ?? 0)}
                  </span>
                </div>
                <div className="ou-highlight-sub">Breadth of engagement</div>
              </div>
            </div>
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--amber">
                <FaSignInAlt />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Last sign-in</div>
                <div className="ou-highlight-value">
                  {user?.lastSignInTime
                    ? formatRelative(new Date(user.lastSignInTime).getTime())
                    : "—"}
                </div>
                <div className="ou-highlight-sub">
                  {user?.lastSignInTime
                    ? formatDate(new Date(user.lastSignInTime).getTime())
                    : "Never recorded"}
                </div>
              </div>
            </div>
          </div>

          <div className="ou-charts-row">
            <section className="ou-panel">
              <div className="ou-panel-head">
                <div>
                  <h3 className="ou-panel-title">
                    <FaChartLine className="ou-panel-title-icon" />
                    Activity over time
                  </h3>
                  <p className="ou-panel-sub">Daily event count.</p>
                </div>
              </div>
              <div className="ou-panel-body">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={profile.usageOverTime || []}
                    margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip {...chartTooltipProps} />
                    <Line
                      type="monotone"
                      dataKey="events"
                      stroke="#4f46e5"
                      strokeWidth={2.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="ou-panel">
              <div className="ou-panel-head">
                <div>
                  <h3 className="ou-panel-title">
                    <FaBolt className="ou-panel-title-icon" />
                    Feature mix
                  </h3>
                  <p className="ou-panel-sub">What this user does most.</p>
                </div>
              </div>
              <div className="ou-panel-body">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={(profile.features || []).slice(0, 6)}
                    layout="vertical"
                    margin={{ left: 20, right: 16, top: 4, bottom: 4 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#eef2f7"
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="feature"
                      tick={{ fontSize: 11, fill: "#475569" }}
                      width={140}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      {...chartTooltipProps}
                      cursor={{ fill: "#f8fafc" }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#ec4899"
                      radius={[0, 6, 6, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          <section className="ou-panel">
            <div className="ou-panel-head">
              <div>
                <h3 className="ou-panel-title">
                  <FaClock className="ou-panel-title-icon" />
                  Recent activity
                </h3>
                <p className="ou-panel-sub">
                  Last {profile.timeline?.length || 0} events in this period.
                </p>
              </div>
            </div>
            {!profile.timeline?.length ? (
              <div
                className="ou-panel-body"
                style={{ paddingBottom: 28, textAlign: "center", color: "var(--ou-muted)" }}
              >
                No activity recorded in this period.
              </div>
            ) : (
              <ul className="ou-timeline">
                {profile.timeline.map((item) => (
                  <li key={item.id || `${item.createdAt}-${item.type}`}>
                    <div className="ou-timeline-time">
                      {formatRelative(item.createdAt)} · {formatDate(item.createdAt)}
                    </div>
                    <div>
                      <span className="ou-timeline-feature">{item.feature}</span>
                      {item.loanId ? (
                        <span className="ou-timeline-loan">
                          Loan {item.loanId.slice(0, 8)}…
                        </span>
                      ) : null}
                    </div>
                    {item.description ? (
                      <div className="ou-timeline-desc">{item.description}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
